type Translate = (key: string) => string;

export const getFileIndex = (t: Translate) => [
  { id: 'b1', n: '01', label: t('Identity') },
  { id: 'b2', n: '02', label: t('Score and limit') },
  { id: 'b3', n: '03', label: t('Payment behaviour') },
  { id: 'b4', n: '04', label: t('Court judgments') },
  { id: 'b5', n: '05', label: t('Registered security') },
  { id: 'b6', n: '06', label: t('Financials') },
  { id: 'b7', n: '07', label: t('Ownership') },
  { id: 'b8', n: '08', label: t('Group structure') },
];

export const getIdentityFields = (t: Translate) => [
  { k: t('Status'), v: t('Active'), tone: 'green' as const },
  { k: t('Legal form'), v: t('Private limited by shares') },
  { k: t('Incorporated'), v: '04 Mar 2011', mono: true },
  { k: t('Sector'), v: 'SIC 25620 · Machining', mono: true },
  {
    k: t('Registered address'),
    v: 'Unit 7, Bilton Industrial Estate, Coventry CV3 4ND, United Kingdom',
    span: true,
  },
  { k: t('VAT number'), v: 'GB 342 8871 05', mono: true },
  { k: t('EIN · US subsidiary'), v: '47-3918204', mono: true },
];

export const getScoreBand = (t: Translate) => t('Low risk of failure, 12 month horizon');
export const SCORE_VALUE = 82;
export const SCORE_HISTORY = [38, 41, 44, 43, 49, 52, 50, 58, 63, 68, 74, 82];
export const LIMIT_VALUE = 2900000;
export const getLimitRows = (t: Translate) => [
  { k: t('Basis'), v: t('Filed accounts, payment behaviour, registered security, group position') },
  { k: t('Field type'), v: t('Modelled') },
  { k: t('Last recalculated'), v: t('14 Jun 2026, on accounts filing') },
  { k: t('Prior limit'), v: '£2,400,000' },
];

export const DBT_VALUE = 14;
export const getDbtStats = (t: Translate) => [
  { k: t('Trade lines'), v: '118' },
  { k: t('Contributors'), v: '31' },
  { k: t('Value on file'), v: '£14.6M' },
  { k: t('Industry median'), v: t('27 days') },
];
export const getAgingBuckets = (t: Translate) => [
  { label: t('Current'), pct: 61, tone: 'green' as const },
  { label: t('1–30 days'), pct: 24, tone: 'blue' as const },
  { label: t('31–60 days'), pct: 9, tone: 'amber' as const },
  { label: t('61–90 days'), pct: 4, tone: 'amber' as const },
  { label: t('90+ days'), pct: 2, tone: 'red' as const },
];

export const JUDGMENTS = [
  {
    court: 'County Court Business Centre',
    region: 'England & Wales',
    caseNo: 'B7K4X229',
    registered: '14 Mar 2024',
    status: 'Satisfied 09 May 2024',
    tone: 'green' as const,
    amount: '£48,120',
    dim: false,
  },
  {
    court: 'Northampton County Court',
    region: 'England & Wales',
    caseNo: 'J2N8P114',
    registered: '02 Aug 2022',
    status: 'Satisfied 30 Aug 2022',
    tone: 'green' as const,
    amount: '£6,480',
    dim: false,
  },
  {
    court: 'Manchester Civil Justice Centre',
    region: 'England & Wales',
    caseNo: 'M4T2R087',
    registered: '19 Jan 2021',
    status: 'Expired from register',
    tone: 'neutral' as const,
    amount: '£22,905',
    dim: true,
  },
];

export const SECURITY = [
  {
    instrument: 'UCC-1',
    ref: '2023 7702318',
    holder: 'Harbor Ridge Capital Partners LP',
    debtor: 'Debtor: Northfield Precision Inc.',
    collateral: 'All assets, blanket lien',
    filed: '06 Mar 2023',
    status: 'Lapses 06 Mar 2028',
    tone: 'blue' as const,
    dim: false,
  },
  {
    instrument: 'UCC-1',
    ref: '2021 4185522',
    holder: 'Meridian Equipment Finance LLC',
    debtor: 'Assigned to Meridian ABL Fund II',
    collateral: '3 CNC machining centres',
    filed: '14 Oct 2021',
    status: 'Continuation window open',
    tone: 'amber' as const,
    dim: false,
  },
  {
    instrument: 'UCC-3',
    ref: '2024 1150847',
    holder: 'Assignment of 2021 4185522',
    debtor: '',
    collateral: 'Amendment only',
    filed: '22 Jan 2024',
    status: 'No effect on lapse date',
    tone: 'neutral' as const,
    dim: false,
  },
  {
    instrument: 'MR01',
    ref: 'Charge 07742118-0009',
    holder: 'Lloyds Bank plc',
    debtor: '',
    collateral: 'Fixed and floating charge',
    filed: '09 Feb 2024',
    status: 'Outstanding',
    tone: 'blue' as const,
    dim: false,
  },
  {
    instrument: 'UCC-1',
    ref: '2019 2288104',
    holder: 'Cedarline Leasing Co.',
    debtor: '',
    collateral: 'Fleet vehicles, 6 units',
    filed: '11 Sep 2019',
    status: 'Lapsed 11 Sep 2024',
    tone: 'neutral' as const,
    dim: true,
  },
];

export const getClock = (t: Translate) => ({
  label: `${t('Perfection clock')} · filing 2021 4185522`,
  filed: `${t('Filed')} 14 Oct 2021`,
  windowOpen: `${t('Window opened')} 14 Apr 2026`,
  lapses: `${t('Lapses')} 14 Oct 2026`,
  fillPct: 95.8,
  windowPct: 10,
});

export const FINANCIALS_YEARS = ['FY21', 'FY22', 'FY23', 'FY24', 'FY25'];
export const getFinancialsRows = (t: Translate) => [
  { label: t('Turnover, £m'), values: ['38.2', '41.6', '47.9', '52.4', '58.1'] },
  { label: t('Gross margin'), values: ['22.4%', '23.1%', '24.8%', '23.2%', '25.6%'] },
  { label: t('EBITDA, £m'), calculated: true, values: ['3.9', '4.4', '5.6', '5.1', '6.8'] },
  { label: t('Net assets, £m'), values: ['11.2', '12.5', '14.8', '15.6', '18.4'] },
  { label: t('Current ratio'), calculated: true, values: ['1.42', '1.51', '1.63', '1.48', '1.66'] },
  { label: t('Gearing'), calculated: true, values: ['48%', '44%', '39%', '41%', '34%'] },
  { label: t('Employees'), values: ['214', '228', '261', '274', '296'] },
];
export const getFinancialsMeta = (t: Translate) => [
  { k: t('Accounts type'), v: t('Full, audited') },
  { k: t('Period end'), v: '31 Dec 2025' },
  { k: t('Filed'), v: '14 Jun 2026' },
  { k: t('Next due'), v: '30 Sep 2027' },
];

export const DIRECTORS = [
  { name: 'Helena M. Ashcroft', role: 'Director, also PSC', appointed: '12 Feb 2016', roles: '4', dim: false },
  { name: 'Daniel R. Okonjo', role: 'Finance director', appointed: '03 Sep 2019', roles: '1', dim: false },
  { name: 'Peter J. Lindqvist', role: 'Non-executive', appointed: '21 Jun 2023', roles: '7', dim: false },
  { name: 'Alan T. Reeve', role: 'Resigned 14 Nov 2025', appointed: '08 Apr 2014', roles: '2', dim: true },
];

export const SHARE_REGISTER = [
  { holder: 'Northfield Industrial Holdings Ltd', sub: 'GB 06188402', cls: 'Ordinary', pct: 74.5 },
  { holder: 'Kestrel Nominees Ltd', sub: 'GB 04920117', cls: 'Ordinary A', pct: 15 },
  { holder: 'Helena M. Ashcroft', sub: 'Individual', cls: 'Ordinary', pct: 8.5 },
  { holder: 'Northfield EBT', sub: '', cls: 'Non-voting', pct: 2 },
];

export const UBO = {
  name: 'Helena M. Ashcroft, 39.7% effective control',
  detail:
    '8.5% held directly, plus 31.2% through a 41.9% interest in Northfield Industrial Holdings Ltd. Chain resolved across two registry filings.',
};

export const GROUP_TIERS: { name: string; reg: string; score: number; highlight?: boolean }[][] = [
  [{ name: 'Northfield Industrial Holdings Ltd', reg: 'GB 06188402', score: 79 }],
  [
    { name: 'Northfield Precision Components Ltd', reg: 'GB 07742118', score: 82, highlight: true },
    { name: 'Northfield Tooling Group BV', reg: 'NL 58119024', score: 71 },
  ],
  [
    { name: 'Northfield Precision Inc.', reg: 'US-DE 6118942', score: 68 },
    { name: 'Northfield Precision Sp. z o.o.', reg: 'PL 0000418822', score: 74 },
    { name: 'Northfield Tooling GmbH', reg: 'DE HRB 214880', score: 63 },
    { name: 'NF Tooling Sdn Bhd', reg: 'MY 201401022914', score: 58 },
  ],
];

export const getGroupRollup = (t: Translate) => [
  { v: '£9.6M', label: t('Aggregate recommended limit across the group') },
  { v: '4 of 7', label: t('Entities carrying registered security') },
  { v: '58–82', label: t('Score range inside one group') },
  { v: '6', label: t('Jurisdictions, three levels deep') },
];
